import { Fragment } from 'react'

import { isEmpty } from 'lodash-es'
import { DataItem, connect, getDataClass } from 'scrivito'

import { CellRendererParams } from '../EntityTable/types'
import { Icon } from '@/Atomic/Atoms/Icon'
import { StringText } from '@/Atomic/Atoms/StringText'
import { ActivityContactRelation } from '@/Data/ActivityContactRelation/ActivityContactRelationDataClass'
import { DataClassName } from '@/Data/const'
import { getDataItemTitle } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getDataItem } from '@/utils/getDataItem'
import { getDataClassIcon } from '@/utils/image/getDataClassIcon'
import './ActivityContactCell.scss'

export type ActivityContactCellParams = CellRendererParams & {
  contactEntity?: DataClassName
  showIcon?: boolean
  /** Override the icon name; defaults to the entity's data class icon. */
  icon?: string
}

const resolveContact = (relation: DataItem): DataItem | null => {
  const dataClass = getDataClass(ensureString(relation.get('contactEntity')))
  const contactId = ensureString(relation.get('contact'))
  if (!dataClass || !contactId) {
    return null
  }
  return getDataItem(dataClass, contactId).dataItem
}

export const ActivityContactCell = connect(
  ({
    dataItem,
    contactEntity = DataClassName.CONTACT,
    showIcon = false,
    icon,
  }: ActivityContactCellParams) => {
    const renderCap = 5

    const relations = ActivityContactRelation.all()
      .transform({
        filters: {
          activity: dataItem.id(),
          contactEntity,
        },
      })
      .take()

    if (isEmpty(relations)) {
      return <span className="activity-contact-inline">-</span>
    }

    const visibleRelations = relations.slice(0, renderCap)
    const remaining = Math.max(0, relations.length - visibleRelations.length)

    return (
      <span className="activity-contact-inline">
        {showIcon && (
          <Icon icon={icon ?? getDataClassIcon(contactEntity)} size="sm" />
        )}
        {visibleRelations.map((relation, index) => {
          const contact = resolveContact(relation)

          let title = getDataItemTitle(contact)
          if (!title) {
            title = ensureString(relation?.get('contact'))
          }

          const label =
            index + 1 < visibleRelations.length || remaining > 0
              ? `${title},`
              : title

          return (
            <Fragment key={relation.id()}>
              <StringText
                wrapperClassName="activity-contact-item"
                variant="link"
                value={label}
                dataItem={contact ?? undefined}
              />
            </Fragment>
          )
        })}
        {remaining > 0 ? (
          <StringText
            wrapperClassName="activity-contact-item activity-contact-more"
            value={`+${remaining}`}
          />
        ) : null}
      </span>
    )
  },
)
